import { useEffect } from 'react'
import formatEuro from '../../../helpers/formatEuro'
import { impactPageUrl } from '../../../helpers/impactInBeeld/impactPageUrl'
import { computeProductImpact } from '../../../helpers/impactInBeeld/computeProductImpact'
import ImpactQrBlock from './ImpactQrBlock.jsx'

/**
 * Print-sectie (tafelkaart, hangtags): preview van de uiting binnen
 * .impact-print-area + de Print-afsluitactie. Bij productkanalen kiest de
 * gebruiker hier welke producten een hangtag krijgen.
 *
 * @param {object} props
 * @param {{id: string, title: string, requirements: Array<string>}} props.channel - Kanaal-config-entry
 * @param {string} props.client - Tenant-CompagnyID (voor de QR-URL's)
 * @param {{Name: string, Logo: string}} props.branding - Organisatienaam + logo voor de preview
 * @param {number|null} props.ratio - Actueel impact-per-euro-kengetal
 * @param {Array<object>} props.allProducts - Alle producten van de tenant
 * @param {Array<object>} props.selectedProducts - De geselecteerde producten
 * @param {(productId: string) => void} props.onToggleProduct - Selectie aan/uit
 */
const PrintChannelSection = ({
    channel,
    client,
    branding,
    ratio,
    allProducts,
    selectedProducts,
    onToggleProduct,
}) => {
    const needsProducts = channel.requirements.includes('producten')
    const origin = window.location.origin

    // De @media print-regels (alles verbergen behalve .impact-print-area) mogen
    // ALLEEN gelden terwijl dit scherm openstaat — anders printen andere
    // printflows (vragenlijst, live report) een lege pagina. Body-class aan bij
    // mount, weg bij unmount.
    useEffect(() => {
        document.body.classList.add('impact-print-mode')
        return () => document.body.classList.remove('impact-print-mode')
    }, [])

    if (needsProducts && (allProducts?.length || 0) === 0) {
        return (
            <div className='impact-in-beeld-empty-state'>
                <p>Je hebt nog geen producten of diensten. Voeg eerst een product toe om er
                een {channel.title.toLowerCase()} voor te maken.</p>
                <a href={`/${client}/impactinbeeld/producten`}>Naar Producten &amp; diensten</a>
            </div>
        )
    }

    return (
        <div className='impact-channel-print-section'>
            {needsProducts && (
                <div className='impact-channel-product-select'>
                    <h3>Producten op de {channel.title.toLowerCase()}</h3>
                    {allProducts.map((product) => (
                        <label key={product.ID} className='impact-channel-product-option'>
                            <input
                                type='checkbox'
                                checked={selectedProducts.some((candidate) => candidate.ID === product.ID)}
                                onChange={() => onToggleProduct(product.ID)}
                            />
                            <span>{product.Name}</span>
                        </label>
                    ))}
                </div>
            )}

            <div className='impact-print-area'>
                {needsProducts ? (
                    selectedProducts.map((product) => (
                        <div key={product.ID} className='impact-print-card'>
                            <h3>{product.Name}</h3>
                            {computeProductImpact(product.Price, ratio) !== null && (
                                <p className='impact-page-open-highlight'>
                                    Dit product = {formatEuro(computeProductImpact(product.Price, ratio))} aan maatschappelijke waarde
                                </p>
                            )}
                            <ImpactQrBlock url={impactPageUrl({ origin, client, slug: product.Slug, scan: true })} />
                        </div>
                    ))
                ) : (
                    <div className='impact-print-card'>
                        {branding.Logo && <img src={branding.Logo} alt='Logo' />}
                        <h3>{branding.Name}</h3>
                        {ratio !== null && (
                            <p className='impact-page-open-highlight'>
                                Elke euro bij ons = {formatEuro(ratio)} aan maatschappelijke waarde
                            </p>
                        )}
                        <ImpactQrBlock url={impactPageUrl({ origin, client, scan: true })} />
                    </div>
                )}
            </div>

            <button
                type='button'
                className='impact-in-beeld-add-button'
                onClick={() => window.print()}
            >
                Print
            </button>
        </div>
    )
}

export default PrintChannelSection
